import React, { CSSProperties, useState } from 'react';
import useTheme from '@hero/branding/theme';

interface AlertBadgeProps {
    message: string;
    size?: 'small' | 'regular' | 'large';
    styles?: CSSProperties;
}

const AlertBadge: React.FC<AlertBadgeProps> = ({ message, size = 'regular', styles }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { colors } = useTheme();

    // Larger icon size mapping
    const iconSize = size === 'small' ? 20 : size === 'large' ? 32 : 26;

    const badgeStyles: CSSProperties = {
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
        ...styles
    };

    const tooltipStyles: CSSProperties = {
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '8px',
        padding: '10px 14px',
        backgroundColor: colors.neutrals.textAlpha,
        color: colors.neutrals.background,
        borderRadius: '6px',
        fontSize: '13px',
        lineHeight: '18px',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        zIndex: 1000,
        opacity: isHovered ? 1 : 0,
        visibility: isHovered ? 'visible' : 'hidden',
        transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'none'
    };

    const arrowStyles: CSSProperties = {
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: `5px solid ${colors.neutrals.textAlpha}`,
        pointerEvents: 'none'
    };

    const AlertBadgeIcon = () => (
        <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            style={{ display: 'block' }}
        >
            <circle cx="12" cy="12" r="10" fill="#DC2626" stroke="#B91C1C" strokeWidth="1" />

            <line
                x1="12"
                y1="7"
                x2="12"
                y2="13"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <circle cx="12" cy="15.5" r="1" fill="#FFFFFF" />
        </svg>
    );

    return (
        <div
            style={badgeStyles}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            role="button"
            tabIndex={0}
            aria-label={message}
        >
            <AlertBadgeIcon />
            <div style={tooltipStyles}>
                {message}
                <div style={arrowStyles} />
            </div>
        </div>
    );
};

export default AlertBadge;
